import { useQuery } from 'react-query';
import { Bond } from '../../types/Bond';
import { BaseAsset, Asset } from '../../types/Asset';
import { Stock } from '../../types/Stock';
import { apifetch } from '../robotapi';
import { getFundApiCall, GetFundResponse } from './getFund';
import { buildEndpoint } from '../endpoints';

const getFundsUrl = buildEndpoint(['funds']);

type GetFundsResponse = { data: { id: number; name: string; category: 'Stock' | 'Bond' }[] };

export const getFundsApiCall = apifetch<GetFundsResponse>(getFundsUrl, 'GET');

export const useGetFunds = () => {
    const getFundsQuery = useQuery<(Stock | Bond)[]>(['funds', 'all'], async () => {
        const funds = await getFundsApiCall();
        const details = await Promise.all(
            funds.data.map(async (fund) => {
                const factSheet = await getFundApiCall(fund.id + '')();
                return convertFundsResponseToFund(fund, factSheet);
            }),
        );

        return details;
    });

    return getFundsQuery;
};

export const convertFundsResponseToFund = (
    response: GetFundsResponse['data'][0],
    factSheetReponse: GetFundResponse,
): Stock | Bond => {
    if (response.category === 'Stock') {
        return convertFundsResponseToStock(response, factSheetReponse);
    }

    if (response.category === 'Bond') {
        return convertFundsResponseToBond(response, factSheetReponse);
    }

    throw Error('Could not convert');
};

const convertFundsResponseToStock = (
    response: GetFundsResponse['data'][0],
    factSheetReponse: GetFundResponse,
): Stock => {
    let stock: Stock = {
        ...response,
        id: response.id + '',
        category: 'Stock',
        referenceDateCharacteristics: factSheetReponse.referenceDateCharacteristics,
        investmentStrategyText: factSheetReponse.investmentStrategyText,
        basicCharacteristics: convertBasicCharacteristics(factSheetReponse),
        riskCharacteristics: convertRiskCharacteristics(factSheetReponse),
        specifics: convertStockSpecifics(factSheetReponse),
        referenceDateHistoricalReturn: factSheetReponse.referenceDateHistoricalReturn,
        historicalReturn: convertHistoricalReturns(factSheetReponse),
        referenceDateHoldings: factSheetReponse.referenceDateHoldings,
        holdings: factSheetReponse.holdings?.map((h) => {
            return {
                id: h.holdingsName,
                weight: h.holdingsWeight * 100,
                name: h.holdingsName,
            };
        }),
        referenceDateRegions: factSheetReponse.referenceDateRegions,
        regions: factSheetReponse.regions.map((r) => {
            return {
                id: r.regionId + '',
                weight: r.regionWeight * 100,
                name: r.regionName,
            };
        }),
        referenceDateSectors: factSheetReponse.referenceDateSectors,
        sectors: factSheetReponse.sectors.map((s) => {
            return {
                id: s.sectorId + '',
                weight: s.sectorWeight * 100,
                name: s.sectorName,
            };
        }),
        dividendPayments: factSheetReponse.dividendPayments.map((p) => {
            return {
                date: p.paymentDate,
                amountPerParticipation: p.amountPerParticipation * 100,
            };
        }),
        urls: {
            essentialInvestmentInformation: factSheetReponse.documentUrls?.essentialInvestmentInformation,
            factsheetInformation: factSheetReponse.documentUrls?.factsheetInformation,
            umbrellaFundInformation: factSheetReponse.documentUrls?.umbrellaFundInformation,
        },
    };

    return stock;
};

const convertFundsResponseToBond = (response: GetFundsResponse['data'][0], factSheetReponse: GetFundResponse): Bond => {
    let bond: Bond = {
        ...response,
        id: response.id + '',
        category: 'Bond',
        referenceDateCharacteristics: factSheetReponse.referenceDateCharacteristics,
        investmentStrategyText: factSheetReponse.investmentStrategyText,
        basicCharacteristics: convertBasicCharacteristics(factSheetReponse),
        riskCharacteristics: convertRiskCharacteristics(factSheetReponse),
        specifics: convertBondSpecifics(factSheetReponse),
        referenceDateHistoricalReturn: factSheetReponse.referenceDateHistoricalReturn,
        historicalReturn: convertHistoricalReturns(factSheetReponse),
        referenceDateQuality: factSheetReponse.referenceDateCreditQuality,
        quality: factSheetReponse.creditQualities.map((q) => {
            return {
                id: q.creditQualityId + '',
                name: q.creditQualityName,
                weight: q.creditQualityWeight * 100,
            };
        }),
        referenceDateMaturity: factSheetReponse.referenceDateMaturity,
        maturity: factSheetReponse.maturities.map((m) => {
            return {
                id: m.maturityId + '',
                name: m.maturityName,
                weight: m.maturityWeight * 100,
            };
        }),
        referenceDateSectors: factSheetReponse.referenceDateSectors,
        sectors: factSheetReponse.sectors.map((r) => {
            return {
                id: r.sectorId + '',
                name: r.sectorName,
                weight: r.sectorWeight * 100,
            };
        }),
        referenceDateRegions: factSheetReponse.referenceDateRegions,
        regions: factSheetReponse.regions.map((m) => {
            return {
                id: m.regionId + '',
                name: m.regionName,
                weight: m.regionWeight * 100,
            };
        }),
        dividendPayments: factSheetReponse.dividendPayments.map((p) => {
            return {
                date: p.paymentDate,
                amountPerParticipation: p.amountPerParticipation * 100,
            };
        }),
        urls: {
            essentialInvestmentInformation: factSheetReponse.documentUrls?.essentialInvestmentInformation,
            factsheetInformation: factSheetReponse.documentUrls?.factsheetInformation,
            umbrellaFundInformation: factSheetReponse.documentUrls?.umbrellaFundInformation,
        },
    };

    return bond;
};

const convertBasicCharacteristics = (factSheetReponse: GetFundResponse): BaseAsset['basicCharacteristics'] => {
    return {
        category: factSheetReponse.basicCharacteristics.category,
        isin: factSheetReponse.basicCharacteristics.isin,
        startDate: factSheetReponse.basicCharacteristics.startDate,
        managementFee: factSheetReponse.basicCharacteristics.managementFee,
        totalAssets: factSheetReponse.basicCharacteristics.totalAssets,
        numberOfParticipations: factSheetReponse.basicCharacteristics.numberOfParticipations,
        notInvestedAssets: factSheetReponse.basicCharacteristics.notInvestedAssets,
        fundManager: factSheetReponse.basicCharacteristics.fundManager,
        benchmark: factSheetReponse.basicCharacteristics.benchmark,
    };
};

const convertRiskCharacteristics = (factSheetReponse: GetFundResponse): BaseAsset['riskCharacteristics'] => {
    return {
        classification: factSheetReponse.riskCharacteristics.riskClassification,
        standardDeviation36M: factSheetReponse.riskCharacteristics.standarddeviation36M,
        sharpeRatio36M: factSheetReponse.riskCharacteristics.sharpeRatio36M,
        beta36M: factSheetReponse.riskCharacteristics.beta36M,
        rSquared36M: factSheetReponse.riskCharacteristics.rSquared36M,
        trackingError12M: factSheetReponse.riskCharacteristics.trackingError12M,
        grossTrackingDifference12M: factSheetReponse.riskCharacteristics.grossTrackingDifference12M,
        netTrackingDifference12M: factSheetReponse.riskCharacteristics.netTrackingDifference12M,
    };
};

const convertStockSpecifics = (factSheetReponse: GetFundResponse): Stock['specifics'] | undefined => {
    if (!factSheetReponse.characteristicsUnderlyingStocks) return undefined;
    return {
        amountStocks: factSheetReponse.characteristicsUnderlyingStocks.numberOfStocks,
        averageMarketCapitalization: factSheetReponse.characteristicsUnderlyingStocks.averageMarketCapitalization,
        dividend: factSheetReponse.characteristicsUnderlyingStocks.dividend,
        rentabilityOwnAssets: factSheetReponse.characteristicsUnderlyingStocks.rentabilityOwnAssets,
        rateBookValueRatio: factSheetReponse.characteristicsUnderlyingStocks.rateBookValueRatio,
        rateProfitRatio: factSheetReponse.characteristicsUnderlyingStocks.rateProfitRatio,
    };
};

const convertBondSpecifics = (factSheetReponse: GetFundResponse): Bond['specifics'] | undefined => {
    if (!factSheetReponse.characteristicsUnderlyingBonds) return undefined;
    return {
        amountBonds: factSheetReponse.characteristicsUnderlyingBonds.numberOfBonds,
        yieldToMaturity: factSheetReponse.characteristicsUnderlyingBonds.yieldToMaturity,
        averageCouponRate: factSheetReponse.characteristicsUnderlyingBonds.averageCouponRate,
        averageDuration: factSheetReponse.characteristicsUnderlyingBonds.averageDuration,
        averageCreditQuality: factSheetReponse.characteristicsUnderlyingBonds.averageCreditQuality,
        averageWeightedDuration: factSheetReponse.characteristicsUnderlyingBonds.averageWeightedDuration,
    };
};

const convertHistoricalReturns = (factSheetReponse: GetFundResponse): Asset['historicalReturn'] => {
    return factSheetReponse.historicalReturns.map((h) => {
        return {
            id: h.historicalReturnTypeId + '',
            beforeCosts: h.returnBeforeCosts * 100,
            afterCosts: h.returnAfterCosts * 100,
            description: h.description,
            benchmark: h.returnBenchmark * 100,
            year: h.year,
        };
    });
};
